import { jsPDF } from "jspdf";
import { inventoryProduct } from "./inventoryProducts";
import { planCategoryPages } from "./inventoryCategories";

export const PDF_LAYOUT = Object.freeze({ width: 210, height: 297, margin: 12, gap: 8, cardWidth: 89, cardHeight: 112, imageHeight: 72, perPage: 4 });
const INK = "#19251F", GOLD = "#B49345", PAPER = "#F8F6F0", MUTED = "#647067";
export const INVENTORY_LOGO = "/inventory-logo.jpg";

export const formatInventoryPrice = value => new Intl.NumberFormat("en-NG", {
  style: "currency", currency: "NGN", minimumFractionDigits: 0, maximumFractionDigits: 2
}).format(Number.isFinite(Number(value)) ? Number(value) : 0);
export const catalogueProducts = items => (Array.isArray(items) ? items : [])
  .filter(item => item && item.isPublished !== false).map(inventoryProduct);
export const cataloguePageCount = count => count > 0 ? 1 + Math.ceil(count / PDF_LAYOUT.perPage) : 0;
export const catalogueCardPosition = index => ({ x: 12 + (index % 2) * 97, y: 33 + Math.floor((index % 4) / 2) * 120 });
export const catalogueImageSources = item => item.images.filter(source => typeof source === "string" && source.trim()).slice(0, 3);

// Missing/failed secondary images never create empty slots inside the fixed image region.
export function catalogueImageRegions(count, x, y, width, height) {
  if (count <= 0) return [];
  if (count === 1) return [{ x, y, width, height }];
  const lowerHeight = 20, gap = 2;
  const regions = [{ x, y, width, height: height - lowerHeight - gap }];
  for (let i = 0; i < Math.min(count - 1, 2); i++) {
    const lowerWidth = count === 2 ? width : (width - gap) / 2;
    regions.push({ x: x + i * (lowerWidth + gap), y: y + height - lowerHeight, width: lowerWidth, height: lowerHeight });
  }
  return regions;
}

export async function loadInventoryImage(source) {
  if (typeof source !== "string" || !source.trim()) return null;
  try {
    const url = new URL(source, window.location.href);
    if (!["http:", "https:", "data:", "blob:"].includes(url.protocol)) return null;
    const response = await fetch(url.href, { mode: "cors", signal: AbortSignal.timeout(20000) });
    if (!response.ok) return null;
    const objectUrl = URL.createObjectURL(await response.blob());
    try {
      const image = new Image();
      image.src = objectUrl;
      await image.decode();
      if (!image.naturalWidth || !image.naturalHeight) return null;
      // Normalize browser-supported formats and cap memory without cropping products.
      const scale = Math.min(1, 1400 / Math.max(image.naturalWidth, image.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext("2d");
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return { data: canvas.toDataURL("image/jpeg", 0.9), width: canvas.width, height: canvas.height };
    } finally { URL.revokeObjectURL(objectUrl); }
  } catch { return null; }
}

export async function preloadInventoryImages(products, logoUrl, loader = loadInventoryImage) {
  const sources = [...new Set([logoUrl, INVENTORY_LOGO, ...products.flatMap(catalogueImageSources)].filter(Boolean))];
  const images = new Map();
  let cursor = 0;
  // Every image settles before PDF creation; bounded decodes protect large catalogues.
  await Promise.all(Array.from({ length: Math.min(4, sources.length) }, async () => {
    while (cursor < sources.length) {
      const source = sources[cursor++];
      try { images.set(source, await loader(source)); } catch { images.set(source, null); }
    }
  }));
  return images;
}

function text(pdf, value, x, y, size = 10, color = INK, font = "helvetica", style = "normal") {
  pdf.setFont(font, style); pdf.setFontSize(size); pdf.setTextColor(color); pdf.text(value, x, y);
}
function imageIn(pdf, image, { x, y, width, height }) {
  if (!image) return;
  const scale = Math.min(width / image.width, height / image.height);
  const w = image.width * scale, h = image.height * scale;
  pdf.addImage(image.data, "JPEG", x + (width - w) / 2, y + (height - h) / 2, w, h);
}
function fittedText(pdf, value, x, y, width, height, initialSize = 10, style = "bold") {
  pdf.setFont("helvetica", style);
  let size = initialSize, lines;
  do {
    pdf.setFontSize(size);
    lines = pdf.splitTextToSize(String(value), width);
    if (lines.length * size * 0.405 <= height) break;
    size -= 0.25;
  } while (size > 1);
  pdf.setTextColor(INK);
  pdf.text(lines, x, y + size * 0.353, { lineHeightFactor: 1.15 });
}
function priceLine(pdf, value, x, y) {
  // Built-in PDF fonts lack Naira; rasterize only this short line at print resolution.
  const canvas = document.createElement("canvas"), context = canvas.getContext("2d");
  const label = formatInventoryPrice(value);
  context.font = "bold 56px Arial";
  canvas.width = Math.ceil(context.measureText(label).width) + 12; canvas.height = 76;
  context.font = "bold 56px Arial"; context.fillStyle = INK;
  context.fillText(label, 6, 57);
  const width = Math.min(81, canvas.width / 12);
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, y, width, width * canvas.height / canvas.width);
}

function cover(pdf, products, images, logoUrl, eventProps = false) {
  pdf.setFillColor(PAPER); pdf.rect(0, 0, 210, 297, "F");
  pdf.setFillColor(GOLD); pdf.rect(12, 15, 1.2, 26, "F");
  imageIn(pdf, images.get(logoUrl) || images.get(INVENTORY_LOGO), { x: 164, y: 13, width: 34, height: 26 });
  text(pdf, "GENTLE EVENTS", 18, 26, 12, INK, "helvetica", "bold");
  text(pdf, eventProps ? "EVENT PROPS INVENTORY" : "EVENT RENTAL INVENTORY", 18, 35, 8, MUTED);
  text(pdf, "Considered pieces.", 12, 64, 29, INK, "times");
  text(pdf, "Memorable occasions.", 12, 78, 29, INK, "times", "italic");
  const photos = [...new Set(products.flatMap(catalogueImageSources))].map(source => images.get(source)).filter(Boolean).slice(0, 3);
  const regions = photos.length < 2
    ? [{ x: 12, y: 94, width: 186, height: 147 }]
    : [{ x: 12, y: 94, width: 112, height: 147 }, ...Array.from({ length: photos.length - 1 }, (_, i) => ({ x: 128, y: 94 + i * 75.5, width: 70, height: photos.length === 2 ? 147 : 71.5 }))];
  photos.forEach((photo, i) => { pdf.setFillColor("#FFFFFF"); const r = regions[i]; pdf.rect(r.x, r.y, r.width, r.height, "F"); imageIn(pdf, photo, r); });
  if (!photos.length) {
    pdf.setFillColor(INK); pdf.rect(12, 94, 186, 147, "F");
    text(pdf, "Your next occasion,", 24, 158, 25, PAPER, "times");
    text(pdf, "beautifully equipped.", 24, 173, 25, PAPER, "times", "italic");
  }
  pdf.setDrawColor(GOLD); pdf.line(12, 256, 198, 256);
  text(pdf, eventProps ? "Distinctive details for unforgettable celebrations." : "Everything you need to bring your event to life.", 12, 268, 11);
  text(pdf, `${products.length} ${eventProps ? "event props" : "rental pieces"}  /  The Gentle Events collection`, 12, 279, 8, MUTED);
}
function card(pdf, item, index, images) {
  const { x, y } = catalogueCardPosition(index);
  pdf.setFillColor("#FFFFFF"); pdf.rect(x, y, 89, 112, "F");
  const loaded = catalogueImageSources(item).map(source => images.get(source)).filter(Boolean);
  const region = { x: x + 2, y: y + 2, width: 85, height: 72 };
  if (loaded.length) {
    catalogueImageRegions(loaded.length, region.x, region.y, region.width, region.height).forEach((r, i) => imageIn(pdf, loaded[i], r));
  } else {
    pdf.setFillColor("#EAE9DF"); pdf.rect(region.x, region.y, region.width, region.height, "F");
    text(pdf, "GENTLE EVENTS", x + 8, y + 34, 10, MUTED, "helvetica", "bold");
    text(pdf, "Image unavailable", x + 8, y + 43, 9, MUTED);
  }
  pdf.setDrawColor("#DDD9CD"); pdf.line(x + 4, y + 77, x + 85, y + 77);
  fittedText(pdf, item.title || "Rental item", x + 4, y + 80, 81, 16);
  text(pdf, `Quantity: ${Number(item.quantity ?? 0).toLocaleString("en-NG")}`, x + 4, y + 101, 9, MUTED);
  priceLine(pdf, item.price, x + 4, y + 103);
}

export async function createInventoryPdf(items, { logoUrl = INVENTORY_LOGO, mainSiteUrl = "", contact = "", eventProps = false } = {}) {
  const products = catalogueProducts(eventProps ? items.filter(item => item.resourceType === "EVENT_PROP" && item.isPublished === true && item.status !== "inactive") : items);
  if (!products.length) throw new Error("There are no published products to export.");
  const images = await preloadInventoryImages(products, logoUrl);
  const pages = planCategoryPages(products);
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true, putOnlyUsedFonts: true });
  pdf.setProperties({ title: eventProps ? "Gentle Events - Event Props Inventory" : "Gentle Events - Event Rental Inventory", author: "Gentle Events" });
  cover(pdf, pages.flatMap(page => page.flatMap(row => row.items)), images, logoUrl, eventProps);
  pages.forEach((rows, pageIndex) => {
    pdf.addPage("a4", "portrait");
    pdf.setFillColor(PAPER); pdf.rect(0, 0, 210, 297, "F");
    text(pdf, eventProps ? "GENTLE EVENTS  /  EVENT PROPS" : "GENTLE EVENTS  /  RENTAL COLLECTION", 12, 16, 9, INK, "helvetica", "bold");
    pdf.setDrawColor(GOLD); pdf.line(12, 22, 198, 22);
    rows.forEach((row, rowIndex) => {
      if (!rowIndex || rows[rowIndex - 1].category.key !== row.category.key) {
        fittedText(pdf, row.category.name.toUpperCase(), 12, 24 + rowIndex * 120, 186, 7, 10);
      }
      row.items.forEach((item, column) => card(pdf, item, rowIndex * 2 + column, images));
    });
    if (pageIndex === pages.length - 1) {
      text(pdf, "Planning an event?", 12, 273, 9, INK, "helvetica", "bold");
      text(pdf, "Browse our collection online or contact Gentle Events for a quotation.", 12, 279, 8, MUTED);
      const details = [mainSiteUrl, contact].filter(Boolean).join("  |  ");
      if (details) fittedText(pdf, details, 12, 282, 164, 7, 8, "normal");
    }
    text(pdf, String(2 + pageIndex).padStart(2, "0"), 190, 288, 9, MUTED);
  });
  return pdf;
}
export async function exportInventory(items, options) {
  const pdf = await createInventoryPdf(items, options);
  pdf.save("Gentle-Events-Inventory.pdf");
  return pdf;
}
export default exportInventory;
